import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import StepperComponent from "components/blocks/stepper/Stepper";
import { useCallback, useState } from "react";
import EnterKey from "./EnterKey";
import ConfirmActivation from "./confirm/ConfirmActivation";
import { ButtonWrapper, ValidateWrapper } from "styles/ModalCommonStyled";
import BigButton from "components/atoms/buttons/BigButton";
import {
  ActivateKey,
  PostCheckActivateResponse,
  SUBSCRIPTION_TYPE,
  SubscriptionOrgListColumnType,
} from "api/interfaces/subscription.interface";
import useStatusMutation from "hook/useStatusMutation";
import { postEuActivateKey, postEuCheckActivateKey } from "api/subscriptionAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import { ValidateWord } from "components/atoms/text/Text";
import { isValidKeyFormat } from "utils/SubscriptionUtil";
import useApiError from "hook/useApiError";
import { useQueryClient } from "react-query";
import SubscriptionOrgLabel from "components/atoms/text/labels/SubscriptionOrgLabel";
import { checkEmpty, toTrim } from "utils/functions";

type Props = {
  row: SubscriptionOrgListColumnType;
  onModal: () => void;
};
const steps = [
  {
    label: "Enter Key",
  },
  {
    label: "Confirm Activation",
  },
];

export default function SubscriptionActivateModal(props: Props): JSX.Element {
  const { row } = props;
  const { handleErrorNotify, intlString, lang, notifySuccess } = useApiError();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(0);
  const [key, setKey] = useState<string>("");

  const [confirmData, setConfirmData] = useState<ActivateKey | null>(null);
  const [userFixedStorage, setUserFixedStorage] = useState<
    number | undefined
  >();
  const [mergeFreeKey, setMergeFreeKey] = useState<string | undefined>();
  const [validateMsg, setValidateMsg] = useState<string>("");

  const mutationCheckActivateKey = useStatusMutation(postEuCheckActivateKey, {
    onSuccess: (res: PostCheckActivateResponse) => {
      if (res.result && res.error === 0) {
        setPage(1);
        setConfirmData({ ...res.result });
        setMergeFreeKey(undefined);
        if (
          res.result.keyType === SUBSCRIPTION_TYPE.STORAGE &&
          !userFixedStorage
        ) {
          setUserFixedStorage(res.result.preActivated);
        }
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.check.activate.fail);
    },
  });

  const mutationActivateKey = useStatusMutation(postEuActivateKey, {
    onSuccess: (res: PostCheckActivateResponse) => {
      notifySuccess(lang.label.subscription.activate.success);
      props.onModal();
      queryClient.invalidateQueries("getSubscriptionDetailSummary");
      queryClient.invalidateQueries("getSubscriptionKeyList");
      queryClient.invalidateQueries("getSubscriptonOrgList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.activate.fail);
    },
  });

  const onValidate = useCallback(() => {
    const trimmedKey = toTrim(key);
    setKey(trimmedKey);
    if (trimmedKey === "") {
      setValidateMsg(intlString(lang.label.subscription.activate.key.empty));
      return false;
    }

    if (!isValidKeyFormat(trimmedKey)) {
      setValidateMsg(intlString(lang.label.subscription.key.format.error));
      return false;
    }
    return true;
  }, [key]);

  const onMutateCheckActivateKey = useCallback(
    ({ key, userFixedStorage }: { key: string; userFixedStorage?: number }) => {
      const payload = {
        key,
        ...(confirmData?.keyType === SUBSCRIPTION_TYPE.STORAGE && {
          userFixedStorage,
        }),
      };
      mutationCheckActivateKey.mutate({
        accountId: row.accountId,
        payload,
      });
    },
    [key, userFixedStorage, mutationCheckActivateKey, row.accountId]
  );

  const checkFirstActivateKey = useCallback(() => {
    if (!mutationCheckActivateKey.idle) {
      return;
    }
    let trimmedKey = toTrim(key);
    setKey(trimmedKey);
    setValidateMsg("");
    onMutateCheckActivateKey({ key: trimmedKey });
  }, [mutationCheckActivateKey.idle, onMutateCheckActivateKey, key]);

  const onActivate = useCallback(() => {
    if (!mutationActivateKey.idle) {
      return;
    }
    if (confirmData !== null) {
      const { mergeableFreeKeys, ...activatePayload } = confirmData;
      mutationActivateKey.mutate({
        accountId: row.accountId,
        payload: {
          ...activatePayload,
          ...(confirmData?.keyType === SUBSCRIPTION_TYPE.STORAGE && {
            userFixedStorage,
          }),
          ...(mergeFreeKey && { mergeFreeKey }),
        },
      });
    }
  }, [confirmData, userFixedStorage, mergeFreeKey, mutationActivateKey, row.accountId]);

  const onChangeKey = useCallback(
    (string: string) => {
      if (isValidKeyFormat(string)) {
        setValidateMsg("");
      }
      setKey(string);
    },
    [setKey]
  );

  const onChangeStorage = (number?: number | null) => {
    if (number === null || number === undefined) return;

    // 소수 입력 시 정수로 변환
    const intVal = Math.round(number); // 반올림

    setUserFixedStorage(intVal);
    if (confirmData?.keyType === SUBSCRIPTION_TYPE.STORAGE) {
      onMutateCheckActivateKey({ key, userFixedStorage: intVal });
    }
  };

  const onChangePage = useCallback(
    (name: string, index?: number) => {
      if (name === "next") {
        if (!onValidate()) {
          return;
        }
        if (page === 0) {
          checkFirstActivateKey();
          return;
        }
        setPage(page + 1);
      }
      if (name === "previous") {
        setPage(page - 1);
      }
      if (name === "save") {
        props.onModal();
      }
      if (name === "step" && index !== undefined) {
        if (!onValidate()) {
          return;
        }
        if (page === 0 && index === 1) {
          checkFirstActivateKey();
          return;
        }
        setPage(index);
      }
    },
    [page, key]
  );

  return (
    <BigPopupTemplate title="Activate Subscription Key" onModal={props.onModal}>
      <StepperComponent
        activeStep={page}
        steps={steps}
        onClick={onChangePage}
      />
      <SubscriptionOrgLabel
        accountName={row.name}
        accountNumber={row.accountNumber}
        accountLevel={row.level}
      />
      <EnterKey page={page} value={key} onChangeKey={onChangeKey} />

      {page === 1 && confirmData !== null && (
        <ConfirmActivation
          data={confirmData}
          userFixedStorage={userFixedStorage}
          onChangeStorage={onChangeStorage}
          mergeFreeKey={mergeFreeKey}
          onChangeMergeFreeKey={setMergeFreeKey}
        />
      )}
      {validateMsg !== "" && (
        <ValidateWrapper>
          <ValidateWord>{validateMsg}</ValidateWord>
        </ValidateWrapper>
      )}
      {page === 1 && (
        <ValidateWrapper>
          <ValidateWord>
            The activated subscription key cannot be deactivated, transferred or
            canceled.
          </ValidateWord>
        </ValidateWrapper>
      )}
      <ButtonWrapper>
        <BigButton
          label={page === 0 ? "Next" : "Previous"}
          buttonType={
            page === 0 && !checkEmpty(key) ? "primary" : "cancel"
          }
          onClickBtn={() => {
            if (page === 0) {
              onChangePage("next");
            } else {
              onChangePage("previous");
            }
          }}
          disabled={page === 0 && key === ""}
        />
        {page === 1 && (
          <BigButton
            label="Activate"
            onClickBtn={onActivate}
            disabled={!mutationActivateKey.idle}
          />
        )}
      </ButtonWrapper>
    </BigPopupTemplate>
  );
}
